import { toClassificationDialogData } from '@content-search/adapters';
import type { ContentCardItem } from '@content-search/domain';
import { useStandardLabels } from '@content-search/hooks/useStandardLabels';
import { ClassificationDialog } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { Card, Button, ButtonSize, ButtonType } from '@soteria/design-system-ve';
import { useMemo, useState } from 'react';

import * as styles from './ContentCard.css';
import { ExpandableDetails } from './ExpandableDetails';
import { SearchStyleContentSummary } from './SearchStyleContentSummary';

export interface ContentCardProps {
  item: ContentCardItem;
  onClick?: () => void;
  mediaServiceUrl?: string;
}

/**
 * Displays a content search result card with summary, expandable details, and action controls.
 */
export function ContentCard({ item, onClick, mediaServiceUrl }: ContentCardProps) {
  const isV2Enabled = useIsFeatureEnabled(FEATURE_FLAGS.CLASSIFICATION_DIALOG_V2);
  const { labelContentTypeMap } = useStandardLabels();
  const [showDialog, setShowDialog] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const isSafeContent = item.classificationResult !== 'rejected';
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafeContent);

  // V1: Show conversation context on card click
  // V2: Open modal on card click
  const handleCardClick = () => {
    if (isV2Enabled) {
      setShowDialog(true);
    } else {
      onClick?.();
    }
  };

  // V2: Show conversation context button handler
  const handleShowContext = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  // V1: Toggle expandable details
  const toggleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDetailsExpanded(prev => !prev);
  };

  const handleToggleBlur = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsContentBlurred(!isContentBlurred);
  };

  // Convert ContentCardItem to ClassificationDialogData for the core dialog
  // Pass label metadata to derive per-label content type when the upstream API doesn't include it
  const dialogData = useMemo(() => toClassificationDialogData(item, labelContentTypeMap), [item, labelContentTypeMap]);

  // V1: Original behavior with ExpandableDetails and "Show Details" button
  // Cards without onClick (e.g., username) use default cursor instead of pointer.
  if (!isV2Enabled) {
    return (
      <Card className={styles.contentCard({ clickable: !!onClick })} onClick={onClick ? handleCardClick : undefined}>
        <SearchStyleContentSummary isContentBlurred={isContentBlurred} item={item} mediaServiceUrl={mediaServiceUrl} onToggleBlur={handleToggleBlur} />
        <ExpandableDetails isContentBlurred={isContentBlurred} isExpanded={isDetailsExpanded} item={item} onToggleBlur={handleToggleBlur} />
        <div className={styles.centeredActionContainer}>
          <Button buttonSize={ButtonSize.Accelerator} buttonType={ButtonType.Tertiary} onClick={toggleDetails}>
            {isDetailsExpanded ? 'Hide Details' : 'Show Details'}
          </Button>
        </div>
      </Card>
    );
  }

  // V2: New behavior with modal on card click and "Show Conversation Context" button
  // Hide the context button when onClick is not provided (e.g., username content has no conversation context)
  const showConversationContext = !!onClick;

  return (
    <>
      <Card className={styles.contentCard()} onClick={handleCardClick}>
        <SearchStyleContentSummary isContentBlurred={isContentBlurred} item={item} mediaServiceUrl={mediaServiceUrl} onToggleBlur={handleToggleBlur} />
        {showConversationContext && (
          <div className={styles.centeredActionContainer}>
            <Button buttonSize={ButtonSize.Accelerator} buttonType={ButtonType.Tertiary} onClick={handleShowContext}>
              Show Conversation Context
            </Button>
          </div>
        )}
      </Card>

      <ClassificationDialog
        data={dialogData}
        isContentBlurred={isContentBlurred}
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onToggleBlur={() => setIsContentBlurred(!isContentBlurred)}
      />
    </>
  );
}
